import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShoppingBag, Heart, Ruler, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/hooks/useWishlist';
import MorseCodeVisualizer from '@/components/MorseCodeVisualizer';
import PocketStoneUpsell from '@/components/PocketStoneUpsell';
import ProductReviews from '@/components/ProductReviews';
import Seo from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pricing rules — base price varies by size, with optional clasp upcharges.
// Up to 19 cm (inclusive): €24.95 · above 19 cm: €26.95.
// Clasp upcharges add to whichever base applies.
const SIZE_OPTIONS = [
  { cm: 17 }, { cm: 17.5 }, { cm: 18 }, { cm: 18.5 }, { cm: 19 },
  { cm: 19.5 }, { cm: 20 }, { cm: 20.5 }, { cm: 21 }, { cm: 21.5 },
  { cm: 22 }, { cm: 22.5 }, { cm: 23 },
];
const CLASPS = [
  { id: 'lobster',          label: 'Lobster claw',                desc: 'Classic, secure, fully adjustable', upcharge: 0 },
  { id: 'magnetic-barrel',  label: 'Magnetic barrel',             desc: 'Discreet snap closure',            upcharge: 2 },
  { id: 'magnetic-heart',   label: 'Magnetic heart (standard)',   desc: 'Snap-shut, perfect for gifts',     upcharge: 4 },
  { id: 'magnetic-heart-l', label: 'Magnetic heart (large)',      desc: 'Bolder heart accent',              upcharge: 6 },
  { id: 'magnetic-star',    label: 'Magnetic star (standard)',    desc: 'Whimsical star closure',           upcharge: 4 },
  { id: 'magnetic-star-l',  label: 'Magnetic star (large)',       desc: 'Statement star closure',           upcharge: 6 },
  { id: 'metal-drawstring', label: 'Metal drawstring',            desc: 'Sliding metal closure',            upcharge: 5 },
];

const basePriceForSize = (sizeCm) => (sizeCm <= 19 ? 24.95 : 26.95);
const claspUpcharge = (claspId) => (CLASPS.find((c) => c.id === claspId)?.upcharge || 0);
const calculatePrice = (sizeCm, claspId) => basePriceForSize(sizeCm) + claspUpcharge(claspId);

export default function ProductDetailPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(null);
  const [sizeCm, setSizeCm] = useState(19);
  const [clasp, setClasp] = useState('lobster');
  const [keepsakeCards, setKeepsakeCards] = useState(null);
  // Lightbox: null when closed, otherwise { url, label } of the card being viewed.
  const [zoomedCard, setZoomedCard] = useState(null);
  const [reviewsMeta, setReviewsMeta] = useState({ count: 0, average: null });
  const { addToCart, loading: cartLoading } = useCart();
  const { has: inWishlist, toggle: toggleWishlist } = useWishlist();

  const unitPrice = calculatePrice(sizeCm, clasp);

  const resolveImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${img}`;
    return img;
  };

  const handleWishlistToggle = () => {
    if (!product) return;
    const nowSaved = toggleWishlist(product.id);
    toast.success(nowSaved ? 'Added to wishlist' : 'Removed from wishlist');
  };

  // Combined gallery: primary image + extras (deduped)
  const gallery = product
    ? [product.image, ...(product.images || [])].filter((v, i, arr) => v && arr.indexOf(v) === i)
    : [];

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const [productRes, cardsRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`, { headers: { 'Cache-Control': 'no-cache' } }),
          axios.get(`${API}/products/${productId}/keepsake-cards`, { headers: { 'Cache-Control': 'no-cache' } }).catch(() => null),
          axios.get(`${API}/products/${productId}/reviews`, { headers: { 'Cache-Control': 'no-cache' } }).catch(() => null),
        ]);
        setProduct(productRes.data);
        setActiveImage(productRes.data.image);
        if (cardsRes?.data?.cards) setKeepsakeCards(cardsRes.data.cards);
        if (reviewsRes?.data) {
          setReviewsMeta({
            count: reviewsRes.data.count || (reviewsRes.data.reviews || []).length,
            average: reviewsRes.data.average || null,
          });
        }
      } catch (error) {
        logger.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const isStone = product?.category === 'pocket-stone';

  const handleAddToCart = async () => {
    for (let i = 0; i < quantity; i++) {
      if (isStone) {
        await addToCart(product.id, 1);
      } else {
        await addToCart(product.id, 1, null, clasp, sizeCm);
      }
    }
    if (isStone) {
      toast.success(`${product.name} added to cart`);
    } else {
      const claspLabel = CLASPS.find((c) => c.id === clasp)?.label || clasp;
      toast.success(`${product.name} · ${sizeCm} cm · ${claspLabel} added to cart`);
    }
  };

  if (loading) {
    return (
      <div className="pt-28 pb-16 min-h-screen">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
            <div className="aspect-[3/4] bg-[#CAD2C5] rounded-sm" />
            <div className="space-y-6">
              <div className="h-8 bg-[#CAD2C5] rounded w-3/4" />
              <div className="h-4 bg-[#CAD2C5] rounded w-full" />
              <div className="h-4 bg-[#CAD2C5] rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-28 pb-16 min-h-screen">
        <div className="container-custom px-6 md:px-12 lg:px-24 text-center">
          <h1 className="font-serif text-2xl text-[#354F52] mb-4">
            Product not found
          </h1>
          <Link to="/products" className="btn-secondary">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16" data-testid="product-detail-page">
      <Seo
        title={product.name}
        description={`${product.name} — ${(product.description || product.product_description || '').slice(0, 140)}`.replace(/\s+/g, ' ').trim()}
        path={`/products/${product.id}`}
        type="product"
        image={product.image && product.image.startsWith('/') ? `https://whisperingwristwear.com${product.image}` : product.image}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Product',
              '@id': `https://whisperingwristwear.com/products/${product.id}#product`,
              name: product.name,
              description: (product.product_description || product.description || '').slice(0, 600),
              image: product.image && product.image.startsWith('/') ? `https://whisperingwristwear.com${product.image}` : product.image,
              brand: { '@type': 'Brand', name: 'Whispering Wristwear' },
              sku: product.id,
              mpn: product.id,
              category: product.category || 'Handmade jewellery',
              ...(product.morse_text ? {
                additionalProperty: [{
                  '@type': 'PropertyValue',
                  name: 'Morse code',
                  value: product.morse_text,
                }],
              } : {}),
              offers: {
                '@type': 'Offer',
                url: `https://whisperingwristwear.com/products/${product.id}`,
                priceCurrency: 'EUR',
                price: (product.price ?? 0).toFixed ? product.price.toFixed(2) : product.price,
                availability: 'https://schema.org/InStock',
                itemCondition: 'https://schema.org/NewCondition',
                seller: { '@type': 'Organization', name: 'Whispering Wristwear' },
                priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
                shippingDetails: {
                  '@type': 'OfferShippingDetails',
                  shippingRate: {
                    '@type': 'MonetaryAmount',
                    value: '0',
                    currency: 'EUR',
                  },
                  shippingDestination: {
                    '@type': 'DefinedRegion',
                    addressRegion: 'EU',
                  },
                },
              },
              ...(reviewsMeta.count > 0 && reviewsMeta.average
                ? {
                    aggregateRating: {
                      '@type': 'AggregateRating',
                      ratingValue: Number(reviewsMeta.average).toFixed(1),
                      reviewCount: reviewsMeta.count,
                      bestRating: 5,
                      worstRating: 1,
                    },
                  }
                : {}),
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://whisperingwristwear.com/' },
                { '@type': 'ListItem', position: 2, name: 'Shop', item: 'https://whisperingwristwear.com/products' },
                { '@type': 'ListItem', position: 3, name: product.name, item: `https://whisperingwristwear.com/products/${product.id}` },
              ],
            },
          ],
        }}
      />
      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Back Link */}
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-8"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-widest">Back to Shop</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Product Image Gallery */}
          <div className="relative">
            <div className="sticky top-28">
              <div className="aspect-[3/4] bg-[#F5F5F0] rounded-sm overflow-hidden">
                <img
                  src={activeImage || product.image}
                  alt={
                    product.category === 'pocket-stone'
                      ? `${product.name} — polished natural pocket stone by Whispering Wristwear, Paphos`
                      : `${product.name} — handmade Morse code bracelet${product.morse_text ? ` spelling "${product.morse_text.toLowerCase()}"` : ''} on ${product.bead_color === 'silver' ? 'silver-tone' : 'warm-gold'} beads · Whispering Wristwear studio, Paphos, Cyprus`
                  }
                  className="w-full h-full object-cover transition-opacity duration-300"
                  data-testid="product-main-image"
                />
              </div>
              {product.featured && (
                <div className="absolute top-4 left-4">
                  <span className="text-caption text-[#D4AF37] bg-[#FAF9F6] px-3 py-1.5 rounded-full text-xs">
                    Bestseller
                  </span>
                </div>
              )}

              {/* Thumbnail strip */}
              {gallery.length > 1 && (
                <div className="mt-4 grid grid-cols-5 gap-2" data-testid="product-thumbnails">
                  {gallery.map((img, idx) => {
                    const isActive = (activeImage || product.image) === img;
                    return (
                      <button
                        key={`${img}-${idx}`}
                        type="button"
                        onClick={() => setActiveImage(img)}
                        className={`aspect-square rounded-sm overflow-hidden bg-[#F5F5F0] border-2 transition-all ${
                          isActive
                            ? 'border-[#D4AF37] opacity-100'
                            : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                        data-testid={`thumbnail-${idx}`}
                      >
                        <img
                          src={img}
                          alt={`${product.name} — alternate view ${idx + 1} (Whispering Wristwear)`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <p className="text-caption text-[#84A98C] mb-2">
              {product.category === 'custom' ? 'Custom' : 
               product.category === 'bestseller' ? 'Bestseller' : 'Collection'}
            </p>
            
            <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-4">
              {product.name}
            </h1>

            <div className="mb-6" data-testid="pdp-price">
              <p className="text-2xl text-[#D4AF37] font-medium leading-tight">
                {!isStone && (
                  <span className="text-sm text-[#84A98C] mr-2 font-normal">From</span>
                )}
                €{(isStone ? (product.price || 0) : unitPrice).toFixed(2)}
              </p>
              {!isStone && (
                <p className="text-xs text-[#84A98C] mt-1">
                  {sizeCm} cm · {CLASPS.find((c) => c.id === clasp)?.label}
                  {claspUpcharge(clasp) > 0 && (
                    <span> · base €{basePriceForSize(sizeCm).toFixed(2)} + €{claspUpcharge(clasp).toFixed(2)} clasp</span>
                  )}
                </p>
              )}
              {isStone && (
                <p className="text-xs text-[#84A98C] mt-1 italic">
                  Hand-picked stone &middot; ships in a small organza pouch with an intention card
                </p>
              )}
            </div>

            {/* Three notes — inspiration, product description, gift message */}
            {(product.inspiration || product.product_description || product.gift_message) ? (
              <div className="space-y-6 mb-8" data-testid="product-notes">
                {product.inspiration && (
                  <div data-testid="note-inspiration">
                    <p className="text-caption text-[#D4AF37] mb-2 flex items-center gap-2">
                      <span className="w-6 h-px bg-[#D4AF37]" />
                      Inspiration
                    </p>
                    <p className="font-serif italic text-[#354F52] leading-relaxed whitespace-pre-line">
                      {product.inspiration}
                    </p>
                  </div>
                )}

                {product.product_description && (
                  <div data-testid="note-description">
                    <p className="text-caption text-[#84A98C] mb-2 flex items-center gap-2">
                      <span className="w-6 h-px bg-[#84A98C]" />
                      Product Details
                    </p>
                    <p className="text-[#52796F] leading-relaxed whitespace-pre-line">
                      {product.product_description}
                    </p>
                  </div>
                )}

                {product.gift_message && (
                  <details className="bg-[#F5F5F0] rounded-sm p-5 group" data-testid="note-gift">
                    <summary className="cursor-pointer flex items-center justify-between gap-2 list-none">
                      <span className="text-caption text-[#D4AF37] flex items-center gap-2">
                        <span className="w-6 h-px bg-[#D4AF37]" />
                        A Personal Message for the Wearer
                      </span>
                      <span className="text-xs text-[#84A98C] group-open:rotate-180 transition-transform">▾</span>
                    </summary>
                    <p className="mt-4 text-[#52796F] leading-relaxed whitespace-pre-line font-serif italic">
                      {product.gift_message}
                    </p>
                    <p className="mt-3 text-[10px] uppercase tracking-wider text-[#84A98C]">
                      Printed on the keepsake card that ships with your order
                    </p>
                  </details>
                )}
              </div>
            ) : (
              <p className="text-[#52796F] leading-relaxed mb-8 whitespace-pre-line">
                {product.description}
              </p>
            )}

            {/* Morse Code Preview (or Intention badge for stones) */}
            <div className="bg-[#F5F5F0] p-6 rounded-sm mb-8">
              <p className="text-caption text-[#84A98C] mb-2">
                {isStone ? (
                  <>The intention: &ldquo;{product.morse_text}&rdquo;</>
                ) : (
                  <>&ldquo;{product.morse_text}&rdquo; in Morse Code</>
                )}
              </p>
              <MorseCodeVisualizer text={product.morse_text} size="md" />
            </div>

            {/* Size Selector */}
            {!isStone && (
            <div className="mb-6" data-testid="size-selector-pdp">
              <div className="flex items-center justify-between mb-3">
                <p className="text-caption text-[#84A98C]">Choose your wrist size</p>
                <Link
                  to="/sizing-guide"
                  className="text-xs text-[#84A98C] hover:text-[#D4AF37] underline decoration-[#D4AF37]/30 underline-offset-2"
                >
                  Measure your wrist →
                </Link>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {SIZE_OPTIONS.map((s) => {
                  const active = sizeCm === s.cm;
                  const isLarge = s.cm > 19;
                  return (
                    <button
                      key={s.cm}
                      type="button"
                      onClick={() => setSizeCm(s.cm)}
                      className={`text-xs py-2 rounded-sm border transition-all ${
                        active
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10 ring-1 ring-[#D4AF37] text-[#354F52]'
                          : 'border-[#CAD2C5] hover:border-[#84A98C] bg-[#FAF9F6] text-[#52796F]'
                      }`}
                      data-testid={`size-option-${s.cm}`}
                      aria-pressed={active}
                      title={isLarge ? 'Above 19 cm: €26.95 base' : 'Up to 19 cm: €24.95 base'}
                    >
                      {s.cm} cm
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#84A98C] mt-2 italic">
                Up to 19 cm: €24.95 base · above 19 cm: €26.95 base.
              </p>
            </div>
            )}

            {/* Clasp Selector */}
            {!isStone && (
            <div className="mb-8" data-testid="clasp-selector">
              <p className="text-caption text-[#84A98C] mb-3">Choose your clasp</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CLASPS.map((c) => {
                  const active = clasp === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setClasp(c.id)}
                      className={`text-left p-3 rounded-sm border transition-all ${
                        active
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10 ring-1 ring-[#D4AF37]'
                          : 'border-[#CAD2C5] hover:border-[#84A98C] bg-[#FAF9F6]'
                      }`}
                      data-testid={`clasp-option-${c.id}`}
                      aria-pressed={active}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-sm font-medium text-[#354F52]">
                          {c.label}
                        </p>
                        {c.upcharge > 0 && (
                          <span className="text-xs text-[#D4AF37] font-medium whitespace-nowrap">
                            +€{c.upcharge.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-[#84A98C] mt-0.5 leading-snug">
                        {c.desc}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              {/* Quantity Selector */}
              <div className="flex items-center border border-[#CAD2C5] rounded-full">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-[#F5F5F0] rounded-l-full transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4 text-[#354F52]" strokeWidth={1.5} />
                </button>
                <span className="px-6 text-[#354F52] font-medium" data-testid="quantity-display">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-3 hover:bg-[#F5F5F0] rounded-r-full transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4 text-[#354F52]" strokeWidth={1.5} />
                </button>
              </div>

              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCart}
                disabled={cartLoading}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
                Add to Cart
              </Button>

              {/* Wishlist Button */}
              <button
                onClick={handleWishlistToggle}
                className={`p-3 border rounded-full transition-colors ${
                  inWishlist(product.id)
                    ? 'bg-[#D4AF37]/15 border-[#D4AF37]'
                    : 'border-[#CAD2C5] hover:bg-[#F5F5F0]'
                }`}
                data-testid="wishlist-btn"
                aria-pressed={inWishlist(product.id)}
              >
                <Heart
                  className={`w-5 h-5 ${
                    inWishlist(product.id) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#354F52]'
                  }`}
                  strokeWidth={1.5}
                />
              </button>
            </div>

            {/* Sizing helper */}
            {!isStone && (
            <div className="-mt-2">
              <Link
                to="/sizing-guide"
                className="text-xs text-[#84A98C] hover:text-[#D4AF37] underline decoration-[#D4AF37]/30 underline-offset-2 transition-colors inline-flex items-center gap-1"
                data-testid="pdp-sizing-guide-link"
              >
                <Ruler className="w-3.5 h-3.5" strokeWidth={1.5} />
                Not sure of your size? Measure your wrist
              </Link>
            </div>
            )}

            {/* Complete the Intention — pocket stone upsell (hidden on stone PDPs themselves
                and on any product where the admin has toggled `show_addons` off). */}
            {!isStone && product.show_addons !== false && (
              <PocketStoneUpsell productId={product.id} />
            )}
            {/* Product Details */}
            <div className="border-t border-[#CAD2C5]/50 pt-8 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <p className="text-[#52796F]">
                  <span className="text-[#354F52] font-medium">Handmade:</span>{' '}
                  {isStone
                    ? 'Hand-picked by Lindy in her Paphos studio'
                    : 'Each bracelet is hand-threaded by Lindy'}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <p className="text-[#52796F]">
                  <span className="text-[#354F52] font-medium">Materials:</span>{' '}
                  {isStone
                    ? 'Natural polished stone, approximately 2–3 cm. Each is unique.'
                    : 'Silver or gold coloured beads on a 7-strand steel wire'}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <p className="text-[#52796F]">
                  <span className="text-[#354F52] font-medium">Includes:</span>{' '}
                  {isStone
                    ? 'Small organza pouch and a printed intention card'
                    : 'Meaning card explaining your Morse code message'}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2" />
                <p className="text-[#52796F]">
                  <span className="text-[#354F52] font-medium">Shipping:</span> Ships within 3-5 business days
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* What's inside the box — keepsake card preview.
            Admins can hide this per product via `show_keepsake_cards`. */}
        {product.show_keepsake_cards !== false && keepsakeCards && (keepsakeCards.inspiration || keepsakeCards.details || keepsakeCards.message) && (
          <section className="mt-16 lg:mt-20" data-testid="keepsake-cards-preview">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <span className="text-caption text-[#D4AF37] mb-2 block">What&apos;s inside the box</span>
              <h2 className="font-serif text-3xl text-[#354F52] mb-3">
                Three little cards tucked in with your bracelet
              </h2>
              <p className="text-[#52796F] leading-relaxed">
                Every piece arrives with three printed cards — the story that inspired it,
                a short note about the craftsmanship, and a quiet message from the studio.
                Keep them, frame them, or pass them on with the gift.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { key: 'inspiration', label: 'Inspiration' },
                { key: 'details', label: 'About this piece' },
                { key: 'message', label: 'A little message' },
              ].map(({ key, label }) => {
                const url = keepsakeCards[key];
                if (!url) return null;
                return (
                  <figure
                    key={key}
                    className="flex flex-col items-center"
                    data-testid={`keepsake-preview-${key}`}
                  >
                    <button
                      type="button"
                      onClick={() => setZoomedCard({ url, label })}
                      className="w-full aspect-square rounded-sm overflow-hidden border border-[#CAD2C5]/60 bg-white shadow-sm group cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] transition-shadow hover:shadow-md"
                      aria-label={`Zoom into ${label} card`}
                      data-testid={`keepsake-zoom-${key}`}
                    >
                      <img
                        src={resolveImageUrl(url)}
                        alt={`${label} card`}
                        className="w-full h-full object-contain transition-transform duration-500 ease-out group-hover:scale-105"
                        loading="lazy"
                      />
                    </button>
                    <figcaption className="text-caption text-[#84A98C] mt-3">
                      {label}
                      <span className="sr-only"> — click to read</span>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </section>
        )}

        {/* Reviews */}
        <ProductReviews productId={product.id} />
      </div>

      {/* Keepsake card lightbox — opens full-size so the text is readable */}
      <Dialog
        open={!!zoomedCard}
        onOpenChange={(o) => !o && setZoomedCard(null)}
      >
        <DialogContent
          className="max-w-3xl p-0 bg-transparent border-none shadow-none [&>button.absolute]:hidden"
          data-testid="keepsake-lightbox"
        >
          {zoomedCard && (
            <div className="relative bg-white rounded-sm overflow-hidden shadow-2xl">
              <button
                type="button"
                onClick={() => setZoomedCard(null)}
                className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/95 text-[#354F52] hover:bg-white shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] transition"
                aria-label="Close keepsake card preview"
                data-testid="keepsake-lightbox-close"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <img
                src={resolveImageUrl(zoomedCard.url)}
                alt={`${zoomedCard.label} card`}
                className="w-full h-auto max-h-[85vh] object-contain"
              />
              <p className="text-caption text-[#84A98C] text-center py-3 border-t border-[#CAD2C5]/40">
                {zoomedCard.label}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
